import { api, ApiError } from './api';
import { authService } from './auth';
import { useAuthStore } from '@/stores/authStore';

/**
 * Architectural invariant: an arbitrary API 401 must NEVER directly hard-logout.
 *
 * Any single endpoint can 401 for reasons that don't mean the session is dead —
 * a cookie-only route the JWT can't satisfy, an ambient widget's poll, a
 * route-stub test harness, a transient blip. Trusting one such 401 to tear down
 * the whole app is the bug that produced the "logged out at random" churn. So
 * instead of logging out on the raw 401, we revalidate ONCE against the source
 * of truth (`GET /auth/me`) and only log out if THAT genuinely fails.
 *
 * Revalidation is single-flight: while one `/auth/me` check is in flight, every
 * further 401 coalesces into the same promise, so a storm of failing queries
 * triggers exactly one revalidation — not one per query, and never a redirect
 * storm.
 *
 * `/auth/me` is called with `suppressUnauthorized` (via
 * `authService.getCurrentUser`), so a 401 from the revalidation itself does NOT
 * re-enter this handler — that closes the infinite-loop risk when the session
 * really is dead.
 */

let inFlight: Promise<void> | null = null;

function hardLogout(): void {
  useAuthStore.getState().logout();
  api.setToken(null);
  window.location.replace('/login');
}

async function revalidateSession(): Promise<void> {
  try {
    // Suppressed 401 → won't recurse into onUnauthorized. Session still valid:
    // leave the original 401 as the calling component's local error and return.
    await authService.getCurrentUser();
  } catch (err) {
    // Only a genuine 401 proves the session is dead. Any other failure — the
    // /auth/me request aborted by a hard navigation tearing down in-flight
    // fetches, a network blip, a 5xx — must NOT clear the token. Doing so is
    // the bounce-to-login bug: an aborted revalidation on the outgoing page
    // would wipe the token the incoming page is about to rehydrate from. In
    // that case leave the session intact; the next genuine 401 re-triggers
    // revalidation via the reset single-flight latch.
    if (err instanceof ApiError && err.status === 401) {
      hardLogout();
    }
  }
}

/**
 * Global `onUnauthorized` handler. Revalidates the session once (single-flight)
 * and only logs out if the session is genuinely dead. Concurrent 401s share the
 * one in-flight revalidation.
 */
export function handleUnauthorized(): Promise<void> {
  if (!inFlight) {
    inFlight = revalidateSession().finally(() => {
      inFlight = null;
    });
  }
  return inFlight;
}

/** Test-only: reset the single-flight latch between cases. */
export function __resetSessionGuardForTests(): void {
  inFlight = null;
}
